/* eslint-disable no-case-declarations */

import {
  CLEAR_TEST_CONNECTION_STATE,
  GET_AGREEMENTS_ASYNC,
  GET_ALL_IRONSCALES_COMPANIES_ASYNC,
  GET_BOARD_STATUSES_ASYNC,
  GET_COMPANIES_ASYNC,
  GET_INTEGRATION_ASYNC,
  GET_PRIORITIES_ASYNC,
  GET_PRODUCTS_ASYNC,
  GET_SERVICE_BOARDS_ASYNC,
  GET_UNMAPPED_IRONSCALES_COMPANIES_ASYNC,
  initialFormState,
  initialProductMappingState,
  LOAD_MORE_COMPANIES_ASYNC,
  PLAN_NAME_MAPPING,
  RESET_FORM,
  SAVE_INTEGRATION_ASYNC,
  SEARCH_COMPANIES_ASYNC,
  SET_CONNECTION_EDIT_MODE,
  SET_CURRENT_STEP,
  SET_PAGE,
  TEST_CONNECTION_ASYNC,
  UPDATE_FORM_FIELD,
  WIZARD_STEPS,
} from './constants'

const setNestedProperty = (obj, path, value) => {
  const keys = path.split('.')

  if (keys.length === 1) {
    const newObj = { ...obj }
    newObj[keys[0]] = value
    return newObj
  }

  // For nested paths, recursively create new objects
  const [firstKey, ...restKeys] = keys
  const restPath = restKeys.join('.')

  // For nested objects, recursively update
  return {
    ...obj,
    [firstKey]: setNestedProperty(obj[firstKey] || {}, restPath, value),
  }
}

const mapCompany = (company) => ({
  id: company.value,
  ironscalesCompany: company.label,
  connectwiseCompany: company.connectwiseCompany || '',
  connectwiseAgreement: company.connectwiseAgreement || '',
  syncEnabled: company.syncEnabled || false,
  availableAgreements: company.availableAgreements || [],
  cwCompanyName: company.cwCompanyName || '',
  cwCompanyStatusName: company.cwCompanyStatusName || '',
  cwCompanyDisallowSaving: company.cwCompanyDisallowSaving === true,
})

const mapSearchRow = (row) => ({
  id: row.id ?? row.value,
  value: String(row.id ?? row.value),
  label: row.name ?? row.label ?? '',
  name: row.name ?? row.label ?? '',
  identifier: row.identifier ?? '',
  disallow_saving: row.disallow_saving === true || row.disallowSaving === true,
  status_name: row.status_name ?? row.statusName ?? '',
})

const getCompaniesListFromPayload = (payload) => {
  if (!payload) return { data: [], total: 0, page: 1 }
  const data = Array.isArray(payload) ? payload : payload.data
  const list = Array.isArray(data) ? data : []
  const total = typeof payload.total === 'number' ? payload.total : list.length
  const page = typeof payload.page === 'number' && payload.page >= 1 ? payload.page : 1
  return { data: list, total, page }
}

export default function connectWiseReducer(state = initialFormState, action) {
  switch (action.type) {
    // Form field updates
    case UPDATE_FORM_FIELD: {
      const { field, value } = action.payload
      return setNestedProperty({ ...state }, field, value)
    }

    case SET_CURRENT_STEP:
      return {
        ...state,
        currentStep: action.payload,
      }

    case SET_CONNECTION_EDIT_MODE:
      return {
        ...state,
        isConnectionEditMode: action.payload,
        connection: {
          ...state.connection,
          private_key: '',
        },
        // Clear test connection state when toggling edit mode
        testConnectionSuccess: false,
        testConnectionError: null,
      }

    case RESET_FORM:
      return initialFormState

    // Test Connection handling
    case TEST_CONNECTION_ASYNC.INITIATE:
      return {
        ...state,
        testConnectionLoading: true,
        testConnectionError: null,
        testConnectionSuccess: false,
      }

    case TEST_CONNECTION_ASYNC.SUCCESS:
      return {
        ...state,
        testConnectionLoading: false,
        testConnectionSuccess: true,
        connection: {
          ...state.connection,
          tested: true,
        },
      }

    case TEST_CONNECTION_ASYNC.ERROR:
      return {
        ...state,
        testConnectionLoading: false,
        testConnectionError: action.error.response.message,
        testConnectionSuccess: false,
      }

    case CLEAR_TEST_CONNECTION_STATE:
      return {
        ...state,
        testConnectionLoading: false,
        testConnectionError: null,
        testConnectionSuccess: false,
        connection: {
          ...state.connection,
          tested: false,
        },
      }

    // Get Companies
    case GET_COMPANIES_ASYNC.INITIATE:
      return state

    case GET_COMPANIES_ASYNC.SUCCESS: {
      const payload = action.payload
      const companies = Array.isArray(payload) ? payload : (payload?.data ?? [])
      return {
        ...state,
        companies: Array.isArray(companies) ? companies : [],
      }
    }

    case GET_COMPANIES_ASYNC.ERROR:
      return state

    case SEARCH_COMPANIES_ASYNC.INITIATE:
      return {
        ...state,
        companiesSearchLoader: true,
      }

    case SEARCH_COMPANIES_ASYNC.SUCCESS: {
      const { q = '', page = 1, results = [], hasMore = false } = action.payload || {}
      // SEARCH always replaces (fresh query result set).
      return {
        ...state,
        companies: results.map(mapSearchRow),
        companiesSearchQuery: q,
        companiesSearchPage: page,
        companiesSearchHasMore: hasMore,
        companiesSearchLoader: false,
      }
    }

    case SEARCH_COMPANIES_ASYNC.ERROR:
      return { ...state, companiesSearchLoader: false }

    case LOAD_MORE_COMPANIES_ASYNC.INITIATE:
      return { ...state, companiesSearchLoader: true }

    case LOAD_MORE_COMPANIES_ASYNC.SUCCESS: {
      const { q = '', page = 2, results = [], hasMore = false } = action.payload || {}
      // Ignore stale page if query has moved on (typing happened after dispatch)
      if (q !== (state.companiesSearchQuery || '')) {
        return { ...state, companiesSearchLoader: false }
      }
      const previous = state.companies || []
      const seen = new Set(previous.map((c) => String(c.id ?? c.value)))
      const incoming = results.map(mapSearchRow).filter((row) => !seen.has(String(row.id)))
      const newPage = Math.max(state.companiesSearchPage || 1, page)
      return {
        ...state,
        companies: [...previous, ...incoming],
        companiesSearchPage: newPage,
        companiesSearchHasMore: page >= newPage ? hasMore : state.companiesSearchHasMore,
        companiesSearchLoader: false,
      }
    }

    case LOAD_MORE_COMPANIES_ASYNC.ERROR:
      return { ...state, companiesSearchLoader: false }

    // Get IRONSCALES Companies
    case GET_ALL_IRONSCALES_COMPANIES_ASYNC.INITIATE:
      return {
        ...state,
        allCompanyLoader: true,
      }

    case GET_ALL_IRONSCALES_COMPANIES_ASYNC.SUCCESS: {
      const { data: list, total, page: responsePage } = getCompaniesListFromPayload(action.payload)
      return {
        ...state,
        allCompanyLoader: false,
        allCompanies: list.map(mapCompany),
        allCompaniesCount: total,
        page: responsePage,
      }
    }

    case GET_UNMAPPED_IRONSCALES_COMPANIES_ASYNC.INITIATE:
      return {
        ...state,
        unmappedCompanyLoader: true,
      }
    case GET_UNMAPPED_IRONSCALES_COMPANIES_ASYNC.SUCCESS: {
      const { data: list, total, page: responsePage } = getCompaniesListFromPayload(action.payload)
      return {
        ...state,
        unmappedCompanyLoader: false,
        unmappedCompanies: list.map(mapCompany),
        unmappedCompaniesCount: total,
        page: responsePage,
      }
    }
    case GET_UNMAPPED_IRONSCALES_COMPANIES_ASYNC.ERROR:
      return {
        ...state,
        unmappedCompanyLoader: false,
      }

    case GET_ALL_IRONSCALES_COMPANIES_ASYNC.ERROR:
      return {
        ...state,
        allCompanyLoader: false,
      }

    // Get Agreements
    case GET_AGREEMENTS_ASYNC.INITIATE:
      return {
        ...state,
        agreementsLoaders: [
          ...new Set([...state.agreementsLoaders, action.payload.ironScalesCompanyId]),
        ],
      }

    case GET_AGREEMENTS_ASYNC.SUCCESS:
      return {
        ...state,
        agreementsLoaders: state.agreementsLoaders.filter((id) => id !== action.payload.companyId),
        mappedCompanies: state.mappedCompanies.map((company) => {
          if (company.id === action.payload.companyId) {
            return {
              ...company,
              availableAgreements: action.payload.data,
            }
          }

          return company
        }),
      }

    case GET_AGREEMENTS_ASYNC.ERROR:
      return {
        ...state,
        agreementsLoaders: state.agreementsLoaders.filter((id) => id !== action.error?.companyId),
      }

    // Get Products
    case GET_PRODUCTS_ASYNC.INITIATE:
      return state

    case GET_PRODUCTS_ASYNC.SUCCESS: {
      const payload = action.payload
      const products = Array.isArray(payload) ? payload : (payload?.data ?? [])
      return {
        ...state,
        products: Array.isArray(products) ? products : [],
      }
    }

    case GET_PRODUCTS_ASYNC.ERROR:
      return state

    // Get Service Boards
    case GET_SERVICE_BOARDS_ASYNC.INITIATE:
      return state

    case GET_SERVICE_BOARDS_ASYNC.SUCCESS: {
      const payload = action.payload
      const serviceBoards = Array.isArray(payload) ? payload : (payload?.data ?? [])
      return {
        ...state,
        serviceBoards: Array.isArray(serviceBoards) ? serviceBoards : [],
      }
    }

    case GET_SERVICE_BOARDS_ASYNC.ERROR:
      return state

    // Get Board Statuses
    case GET_BOARD_STATUSES_ASYNC.INITIATE:
      return state

    case GET_BOARD_STATUSES_ASYNC.SUCCESS: {
      const payload = action.payload
      const boardStatuses = Array.isArray(payload) ? payload : (payload?.data ?? [])
      return {
        ...state,
        boardStatuses: Array.isArray(boardStatuses) ? boardStatuses : [],
      }
    }

    case GET_BOARD_STATUSES_ASYNC.ERROR:
      return state

    // Get Priorities
    case GET_PRIORITIES_ASYNC.INITIATE:
      return state

    case GET_PRIORITIES_ASYNC.SUCCESS: {
      const payload = action.payload
      const priorities = Array.isArray(payload) ? payload : (payload?.data ?? [])
      return {
        ...state,
        priorities: Array.isArray(priorities) ? priorities : [],
      }
    }

    case GET_PRIORITIES_ASYNC.ERROR:
      return state

    // Get Integration (load existing config)
    case GET_INTEGRATION_ASYNC.INITIATE:
      return {
        ...state,
        isLoadingIntegration: true,
      }

    case GET_INTEGRATION_ASYNC.SUCCESS:
      const integrationPayload = action.payload || {}
      const integrationResponse = integrationPayload.response ?? integrationPayload
      const integrationData = integrationResponse?.data ?? integrationResponse
      const productMappingsTemplate =
        Array.isArray(integrationPayload.productMappingsTemplate) &&
        integrationPayload.productMappingsTemplate.length > 0
          ? integrationPayload.productMappingsTemplate
          : initialProductMappingState
      if (!integrationData) {
        // No config exists, reset to initial state and first step
        return {
          ...initialFormState,
          isLoadingIntegration: false,
          currentStep: WIZARD_STEPS.CONNECTION,
          page: 1, // Reset page to 1
          isConnectionEditMode: false,
          productMappings: productMappingsTemplate,
        }
      }

      const connectionUrl =
        integrationData.connection_url ||
        integrationData.cw_base_url ||
        integrationData.cwBaseUrl ||
        integrationData.connectionUrl ||
        ''
      const companyId =
        integrationData.cw_company_id ||
        integrationData.cwCompanyId ||
        integrationData.companyId ||
        ''
      const publicKey = integrationData.public_key || integrationData.publicKey || ''
      // Private key is never returned from API - always empty
      const privateKey = ''

      const hasConfigData = connectionUrl && companyId && publicKey

      // Transform company mappings from API format to frontend format
      const companyMappings =
        integrationData.company_mappings || integrationData.companyMappings || []
      const mappedCompanies = companyMappings.map((cm) => ({
        id: cm.ironscales_company_id || cm.ironscalesCompanyId,
        ironscalesCompany: cm.ironscales_company_name || cm.ironscalesCompanyName || '',
        connectwiseCompany: String(cm.cw_company_id || cm.cwCompanyId || ''),
        connectwiseCompanyName: cm.cw_company_name || cm.cwCompanyName || null,
        cwCompanyIdentifier: cm.cw_company_identifier || cm.cwCompanyIdentifier || '',
        cwCompanyName: cm.cw_company_name || cm.cwCompanyName || '',
        connectwiseAgreement:
          cm.cw_agreement_id || cm.cwAgreementId
            ? String(cm.cw_agreement_id || cm.cwAgreementId)
            : '',
        connectwiseAgreementName: cm.cw_agreement_name || cm.cwAgreementName || null,
        syncEnabled: cm.sync_enabled !== false && cm.syncEnabled !== false,
        availableAgreements: [], // Will be loaded when user navigates to company mapping step
      }))

      return {
        ...state,
        isLoadingIntegration: false,
        currentStep: WIZARD_STEPS.CONNECTION, // Always start at first step when configuring
        page: 1, // Reset page to 1 when loading existing integration
        isConnectionEditMode: false, // Reset edit mode when loading config
        testConnectionSuccess: hasConfigData,
        testConnectionLoading: false,
        testConnectionError: null,
        connection: {
          ...state.connection,
          connection_url: connectionUrl,
          company_id: companyId,
          public_key: publicKey,
          private_key: privateKey, // Always empty - never returned from API
          tested: hasConfigData, // Mark as tested if config exists
        },
        productMappings: productMappingsTemplate.map((el) => {
          // Handle both snake_case (from backend) and camelCase (if transformed)
          const productMappings =
            integrationData.product_mappings || integrationData.productMappings || []
          const planName = el.ironscalesPlan || PLAN_NAME_MAPPING[el.label]
          const item = productMappings.find(
            (pm) =>
              (pm.ironscales_plan || pm.ironscalesPlan) === planName &&
              (pm.plan_category || pm.planCategory) === el.category
          )

          if (item) {
            const productId = item.cw_product_id || item.cwProductId
            const productName = item.cw_product_name || item.cwProductName || ''
            return {
              ...el,
              connectwiseProduct: productId ? String(productId) : '',
              connectwiseProductName: productName,
            }
          }
          return el
        }),
        mappedCompanies,
        ticketBoard: (() => {
          // Transform backend format to simplified Phase 1 format
          const backendTicketBoard = integrationData.ticket_board || integrationData.ticketBoard
          if (!backendTicketBoard) {
            // Return clean initial ticket board object instead of stale state
            return {
              serviceBoard: '',
              status: '',
              priority: '',
              defaultBoardId: '',
              boards: [],
              closedStatus: '',
              closedStatusName: '',
            }
          }

          // If backend returns boards array, extract first board/status/priority for Phase 1
          if (backendTicketBoard.boards && Array.isArray(backendTicketBoard.boards)) {
            const firstBoard = backendTicketBoard.boards[0]
            if (firstBoard?.statuses?.length > 0) {
              const firstStatus = firstBoard.statuses[0]
              return {
                serviceBoard:
                  firstBoard.boardId ||
                  firstBoard.board_id ||
                  backendTicketBoard.defaultBoardId ||
                  '',
                serviceBoardName: firstBoard.boardName || firstBoard.board_name || '',
                status: firstStatus.statusId || firstStatus.status_id || '',
                statusName: firstStatus.statusName || firstStatus.status_name || '',
                priority: firstStatus.priorityId || firstStatus.priority_id || '',
                priorityName: firstStatus.priorityName || firstStatus.priority_name || '',
                defaultBoardId:
                  backendTicketBoard.defaultBoardId ||
                  firstBoard.boardId ||
                  firstBoard.board_id ||
                  '',
                boards: backendTicketBoard.boards,
                closedStatus: backendTicketBoard.defaultClosedStatusId
                  ? String(backendTicketBoard.defaultClosedStatusId)
                  : '',
                closedStatusName: backendTicketBoard.defaultClosedStatusName || '',
              }
            }
          }

          // If already in simple format, use it
          if (backendTicketBoard.serviceBoard || backendTicketBoard.defaultBoardId) {
            return {
              serviceBoard:
                backendTicketBoard.serviceBoard || backendTicketBoard.defaultBoardId || '',
              serviceBoardName:
                backendTicketBoard.serviceBoardName || backendTicketBoard.service_board_name || '',
              status: backendTicketBoard.status || '',
              statusName: backendTicketBoard.statusName || backendTicketBoard.status_name || '',
              priority: backendTicketBoard.priority || '',
              priorityName:
                backendTicketBoard.priorityName || backendTicketBoard.priority_name || '',
              defaultBoardId:
                backendTicketBoard.defaultBoardId || backendTicketBoard.serviceBoard || '',
              boards: backendTicketBoard.boards || [],
              closedStatus: backendTicketBoard.defaultClosedStatusId
                ? String(backendTicketBoard.defaultClosedStatusId)
                : '',
              closedStatusName: backendTicketBoard.defaultClosedStatusName || '',
            }
          }

          // Return clean initial object as fallback
          return {
            serviceBoard: '',
            status: '',
            priority: '',
            defaultBoardId: '',
            boards: [],
            closedStatus: '',
            closedStatusName: '',
          }
        })(),
      }

    case GET_INTEGRATION_ASYNC.ERROR:
      return {
        ...state,
        isLoadingIntegration: false,
      }

    // Save Integration
    case SAVE_INTEGRATION_ASYNC.INITIATE:
      return {
        ...state,
        isSubmitting: true,
        submitError: null,
      }

    case SAVE_INTEGRATION_ASYNC.SUCCESS:
      return {
        ...state,
        isSubmitting: false,
      }

    case SAVE_INTEGRATION_ASYNC.ERROR:
      return {
        ...state,
        isSubmitting: false,
        submitError: action.error,
      }
    case SET_PAGE:
      return {
        ...state,
        page: action.payload.page,
      }

    default:
      return state
  }
}
